import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, FileText, Loader2, Receipt, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { AmountInput } from './ui/amount-input';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS = {
  pending_match: { labelKey: 'status.pendingMatch', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  awaiting_lender: { labelKey: 'status.awaitingLender', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  confirmed: { labelKey: 'status.confirmed', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  rejected: { labelKey: 'status.rejected', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  cancelled: { labelKey: 'status.cancelled', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
};

const fmtAmount = (v, cur) => (cur === 'USD'
  ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : `${Math.round(v).toLocaleString('uz-UZ')} UZS`);

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('uz-UZ', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '—');

/**
 * Payments the counterparty reported from the Telegram bot for one debt:
 * status (waiting for the bank / needs your decision / done), the receipt,
 * and confirm / reject for the open ones.
 */
export default function DebtPaymentClaims({ debt, onChanged }) {
  const { t } = useTranslation('debtPaymentClaims');
  const [claims, setClaims] = useState(null);
  const [busy, setBusy] = useState(null);
  const [customAmount, setCustomAmount] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/debts/${debt.id}/payment-claims`, { withCredentials: true });
      setClaims(Array.isArray(res.data) ? res.data : []);
    } catch {
      setClaims([]);
      toast.error(t('toast.loadFailed'));
    }
  }, [debt.id, t]);

  useEffect(() => { load(); }, [load]);

  // body: { inflow_ids } | { amount } | { accept_claimed } — what exactly gets booked
  const decide = async (claim, action, body = {}) => {
    if (action === 'reject' && !window.confirm(t('confirm.reject'))) return;
    setBusy(claim.id);
    try {
      const res = await axios.post(
        `${API_URL}/api/debts/${debt.id}/payment-claims/${claim.id}/${action}`, body, { withCredentials: true },
      );
      toast.success(res.data?.message || t('toast.done'));
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common:status.error'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700" data-testid="debt-payment-claims">
      <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5 mb-3">
        <Receipt className="h-4 w-4" /> {t('title')}
      </h4>
      {claims === null ? (
        <div className="flex justify-center py-3"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : claims.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-2">{t('empty')}</p>
      ) : (
        <div className="space-y-2">
          {claims.map(c => {
            const st = STATUS[c.status] || STATUS.cancelled;
            const open = c.status === 'pending_match' || c.status === 'awaiting_lender';
            return (
              <div key={c.id} className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-xs">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">{fmtAmount(c.amount, c.currency)}</span>
                    <span className="text-slate-500">
                      {c.method === 'cash' ? `💵 ${t('method.cash')}` : c.card_last4 ? `→ •••• ${c.card_last4}` : `→ ${t('method.other')}`}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded font-medium ${st.cls}`}>{t(st.labelKey)}</span>
                  </div>
                  <span className="text-slate-400">{fmtDateTime(c.created_at)}</span>
                </div>
                {c.note && <p className="mt-1 text-slate-500 dark:text-slate-400">ℹ️ {c.note}</p>}
                {c.receipt_check === 'ok' && <p className="mt-1 text-emerald-700 dark:text-emerald-400">🧾 {t('receiptCheck.ok')} ✓</p>}
                {c.receipt_check === 'mismatch' && <p className="mt-1 text-red-600 dark:text-red-400">🧾 {t('receiptCheck.mismatch')}</p>}
                {c.receipt_check === 'unreadable' && <p className="mt-1 text-slate-500">🧾 {t('receiptCheck.unreadable')}</p>}
                {c.status === 'confirmed' && c.received_amount != null && Math.abs(c.received_amount - c.amount) > 0.01 && (
                  <p className="mt-1 text-amber-700 dark:text-amber-400">
                    {t('amounts.reported', { amount: fmtAmount(c.amount, c.currency) })} · {t('amounts.received')} <b>{fmtAmount(c.received_amount, c.currency)}</b>
                  </p>
                )}
                {c.transfers?.length > 0 && (
                  <p className="mt-1 text-slate-500">
                    🏦 {c.transfers.map(i => `${fmtAmount(i.amount, c.currency)} (${fmtDateTime(i.occurred_at)})`).join(' + ')}
                  </p>
                )}
                {open && (
                  <div className="mt-2 space-y-1.5">
                    <p className="text-slate-500">
                      {c.method === 'cash'
                        ? t('hint.cash')
                        : c.card_last4
                          ? (c.window_transfers?.length
                            ? t('hint.cardTransfers', { last4: c.card_last4 })
                            : t('hint.cardNoTransfers', { last4: c.card_last4 }))
                          : t('hint.other')}
                    </p>
                    {c.split_suggestion && (
                      <div className="flex items-center justify-between gap-2 pl-2 p-1.5 rounded bg-emerald-50 dark:bg-emerald-900/10">
                        <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                          {t('splitPaid', { amounts: c.split_suggestion.map(i => fmtAmount(i.amount, c.currency)).join(' + ') })}
                        </span>
                        <Button size="sm" className="h-6 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700" disabled={busy === c.id}
                          onClick={() => decide(c, 'confirm', { inflow_ids: c.split_suggestion.map(i => i.id) })}>
                          {t('actions.accept')}
                        </Button>
                      </div>
                    )}
                    {c.window_transfers?.map(i => (
                      <div key={i.id} className="flex items-center justify-between gap-2 pl-2">
                        <span className={i.exact ? 'font-semibold text-emerald-700 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-300'}>
                          {fmtAmount(i.amount, c.currency)} · {fmtDateTime(i.occurred_at)} {i.exact && `(${t('exactMatch')})`}
                        </span>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={busy === c.id}
                          onClick={() => decide(c, 'confirm', { inflow_ids: [i.id] })}>
                          {t('actions.acceptThis')}
                        </Button>
                      </div>
                    ))}
                    <div className="flex items-center gap-1.5 flex-wrap pt-1">
                      <div className="w-32">
                        <AmountInput className="h-7 text-xs" placeholder={t('customAmountPlaceholder', { currency: c.currency })}
                          value={customAmount[c.id] || ''} onChange={v => setCustomAmount(s => ({ ...s, [c.id]: v }))} />
                      </div>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                        disabled={busy === c.id || !(parseFloat(customAmount[c.id]) > 0)}
                        onClick={() => decide(c, 'confirm', { amount: parseFloat(customAmount[c.id]) })}>
                        {t('actions.recordAmount')}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" disabled={busy === c.id}
                        onClick={() => decide(c, 'confirm', { accept_claimed: true })}
                        title={t('actions.acceptClaimedHint')}>
                        {t('actions.acceptClaimed', { amount: fmtAmount(c.amount, c.currency) })}
                      </Button>
                    </div>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {(c.receipts || []).map((r, n) => (
                    <a
                      key={r.id}
                      href={`${API_URL}/api/debts/${debt.id}/payment-claims/${c.id}/receipts/${r.id}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                      title={r.duplicate ? t('receipt.duplicateHint')
                        : r.check === 'unreadable' ? t('receipt.unreadableHint') : undefined}
                    >
                      <FileText className="h-3.5 w-3.5" /> {c.receipts.length > 1 ? t('receipt.numbered', { n: n + 1 }) : t('receipt.view')}
                      {r.duplicate && <span className="text-amber-600 dark:text-amber-400">({t('receipt.duplicate')})</span>}
                    </a>
                  ))}
                  {open && (
                    <div className="flex items-center gap-1.5 ml-auto">
                      {c.window_transfers?.filter(i => i.exact).length === 1 && (
                        <Button size="sm" className="h-7 px-2.5 gap-1 bg-emerald-600 hover:bg-emerald-700"
                          disabled={busy === c.id} onClick={() => decide(c, 'confirm', { inflow_ids: [c.window_transfers.find(i => i.exact).id] })}>
                          {busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                          {t('actions.received')}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-7 px-2.5 gap-1 text-red-600"
                        disabled={busy === c.id} onClick={() => decide(c, 'reject')}>
                        <XCircle className="h-3.5 w-3.5" /> {t('actions.notReceived')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
